import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Plus,
  BedDouble, Snowflake, Coffee, Phone, ShowerHead, Sparkles,
  Wind, Tv, Wine, Wifi, Clock, Car, MapPin,
} from "lucide-react";
import Reveal from "../components/Reveal";
import { useReservation } from "../components/ReservationModal";
import QuickReservationBar from "../components/QuickReservationBar";
import { hotel, heroSlides, amenities, rooms, restaurant, safranbolu } from "../mock";

const iconMap = {
  BedDouble, Snowflake, Coffee, Phone, ShowerHead, Sparkles,
  Wind, Tv, Wine, Wifi, Clock, Car,
};

const Home = () => {
  const [active, setActive] = useState(0);
  const { openReservation } = useReservation();

  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % heroSlides.length), 5500);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="font-body bg-[#fdfbf7] overflow-hidden">
      {/* ============ HERO ============ */}
      <section className="relative h-screen min-h-[660px] w-full overflow-hidden">
        {heroSlides.map((slide, i) => (
          <div
            key={i}
            className={`hero-slide ${i === active ? "active" : ""}`}
            style={{ backgroundImage: `url(${slide.image})` }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/45" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/45 to-transparent" />

        {/* Left vertical label */}
        <div className="absolute left-6 md:left-9 top-1/2 -translate-y-1/2 z-10 hidden md:block">
          <span className="vtext eyebrow text-white/70 text-[10px] tracking-[0.4em]">
            Safranbolu · Karabük · Türkiye
          </span>
        </div>

        {/* Bottom editorial content */}
        <div className="absolute inset-x-0 bottom-0 z-10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pb-10 md:pb-14">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-end mb-9 md:mb-11">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-4 mb-5">
                  <span className="w-10 h-px bg-[#d9b877]" />
                  <p className="eyebrow text-[#e7cf9c]">{hotel.tagline}</p>
                </div>
                <h1
                  key={active}
                  className="font-display text-white text-[3.2rem] leading-[0.98] md:text-7xl lg:text-[88px] font-500 max-w-4xl animate-fade-up"
                >
                  {heroSlides[active].title}
                </h1>
                <Link
                  to="/odalar"
                  className="inline-flex items-center gap-2 mt-7 text-white/90 text-[0.72rem] uppercase tracking-[0.18em] border-b border-white/40 pb-1 hover:border-[#d9b877] hover:text-white transition-colors"
                >
                  Odaları Keşfet <ArrowRight size={14} />
                </Link>
              </div>

              {/* Slide index */}
              <div className="lg:col-span-4 flex lg:justify-end items-end">
                <div className="flex items-center gap-5">
                  <div className="text-right">
                    <span className="slide-index text-white text-5xl font-500">
                      0{active + 1}
                    </span>
                    <span className="slide-index text-white/40 text-2xl"> / 0{heroSlides.length}</span>
                    <p className="text-white/60 text-[13px] mt-2 max-w-[220px]">
                      {heroSlides[active].subtitle}
                    </p>
                  </div>
                  <div className="slide-progress">
                    <span key={active} />
                  </div>
                </div>
              </div>
            </div>

            {/* Quick reservation bar — inside hero (desktop) */}
            <div className="hidden md:block">
              <QuickReservationBar />
            </div>
          </div>
        </div>
      </section>

      {/* Quick reservation bar — mobile (just below hero) */}
      <div className="md:hidden relative z-30 px-5 -mt-6">
        <QuickReservationBar />
      </div>

      {/* ============ WELCOME (editorial asymmetric) ============ */}
      <section className="relative py-20 md:py-28">
        <span className="index-num absolute right-4 top-10 text-[26vw] md:text-[200px] font-600 pointer-events-none select-none opacity-60 hidden md:block">
          01
        </span>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center relative">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="serif-i text-[#b8893a] text-2xl mb-3">Be Our Guest</p>
            <h2 className="font-display text-[2.6rem] md:text-6xl text-[#2b2620] font-500 leading-[1.02] mb-7">
              Tarihin <span className="serif-i text-[#b8893a]">zarafeti,</span><br />
              modern konforla buluşuyor
            </h2>
            <div className="rule-h max-w-[120px] mb-7" />
            <p className="text-[#6b6459] leading-[1.95] text-[15px] mb-5 max-w-lg">
              {hotel.descriptionShort} Şehir merkezinde, meslek yüksekokuluna ve
              Güzel Sanatlar Fakültesine yürüme mesafesindeki konumumuzla,
              Safranbolu'nun tüm güzelliklerine adımınızı uzattınız.
            </p>
            <p className="text-[#6b6459] leading-[1.95] text-[15px] mb-9 max-w-lg">
              Tarihi Çarşı, İncekaya Su Kemeri, Bulak Mencilis Mağarası ve Yörük
              Köyü'ne yakın mesafede; her detayı özenle düşünülmüş odalarımızda
              unutulmaz bir konaklama sizi bekliyor.
            </p>
            <div className="flex items-stretch gap-8">
              {[
                { n: "UNESCO", l: "Dünya Mirası · 1994" },
                { n: "★★★", l: "Kaliteli Konfor" },
                { n: "Merkez", l: "Tarihi Çarşıya Yakın" },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-8">
                  {i > 0 && <span className="rule-v h-10" />}
                  <div>
                    <p className="font-display text-2xl text-[#2b2620] font-600 whitespace-nowrap">{s.n}</p>
                    <p className="text-[11px] text-[#9a9086] mt-1 tracking-wide whitespace-nowrap">{s.l}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Overlapping image composition */}
          <div className="lg:col-span-7 order-1 lg:order-2 relative">
            <Reveal className="clip-reveal">
              <img
                src={heroSlides[2].image}
                alt="Safranbolu Çelebi Otel"
                className="w-full h-[420px] md:h-[560px] object-cover"
              />
            </Reveal>
            <Reveal delay={220} className="clip-reveal absolute -bottom-12 -left-6 md:-left-16 w-40 md:w-64 hidden sm:block shadow-2xl">
              <img
                src={rooms[0].gallery[3]}
                alt="Oda detayı"
                className="w-full h-40 md:h-56 object-cover border-[6px] border-[#fdfbf7]"
              />
            </Reveal>
            <div className="absolute -top-6 -right-4 md:right-6 bg-[#2b2620] text-white px-7 py-5 hidden md:block">
              <p className="font-display text-4xl text-[#d9b877] font-600 leading-none">1994</p>
              <p className="text-[10px] text-white/60 tracking-[0.2em] uppercase mt-2">UNESCO Mirası</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ROOMS (editorial showcase) ============ */}
      <section className="py-24 md:py-32 bg-[#f3ece0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 md:mb-24">
            <div>
              <p className="serif-i text-[#b8893a] text-2xl mb-2">Konaklama</p>
              <h2 className="font-display text-[2.6rem] md:text-6xl text-[#2b2620] font-500 leading-none">
                Odalarımız
              </h2>
            </div>
            <p className="text-[#6b6459] max-w-sm text-[14px] leading-relaxed md:text-right">
              Doğal tekstil ürünlerinden konforlu yataklar, lüks banyo malzemeleri
              ve zarif dokunuşlarla donatılmış üç seçkin oda tipi.
            </p>
          </Reveal>

          <div className="space-y-24 md:space-y-32">
            {rooms.map((room, i) => {
              const reversed = i % 2 === 1;
              return (
                <Reveal key={room.id}>
                  <div className="showcase grid grid-cols-1 lg:grid-cols-12 items-center gap-y-8">
                    <div className={`lg:col-span-7 showcase-img ${reversed ? "lg:order-2" : ""}`}>
                      <img src={room.cover} alt={room.name} className="w-full h-[360px] md:h-[520px] object-cover" />
                    </div>
                    <div
                      className={`lg:col-span-5 relative z-10 ${
                        reversed ? "lg:order-1 lg:-mr-16 lg:pr-10" : "lg:-ml-16 lg:pl-10"
                      }`}
                    >
                      <div className="bg-[#fdfbf7] p-8 md:p-12 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.35)] relative">
                        <span className="index-num absolute -top-10 right-6 text-[80px] font-600 opacity-70">
                          0{i + 1}
                        </span>
                        <p className="eyebrow text-[#b8893a] mb-4">Oda Tipi</p>
                        <h3 className="font-display text-4xl md:text-5xl text-[#2b2620] font-500 mb-4">
                          {room.name}
                        </h3>
                        <div className="rule-h max-w-[90px] mb-5" />
                        <p className="text-[#6b6459] leading-[1.9] text-[14px] mb-6">{room.short}</p>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-[#8a8073] tracking-wide mb-8">
                          <span>{room.size}</span>
                          <span className="w-1 h-1 bg-[#cbb98d] rounded-full" />
                          <span>{room.capacity}</span>
                          <span className="w-1 h-1 bg-[#cbb98d] rounded-full" />
                          <span>{room.bed}</span>
                        </div>
                        <div className="flex items-center justify-between border-t border-[#e9e0cf] pt-6">
                          <div>
                            <span className="font-display text-3xl text-[#2b2620] font-600">{room.price}</span>
                            <span className="text-[11px] text-[#9a9086] ml-2">/ {room.priceNote}</span>
                          </div>
                          <button onClick={() => openReservation(room.id)} className="link-elegant">
                            Rezerve Et <span className="le-line" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="text-center mt-20">
            <Link to="/odalar" className="link-elegant justify-center">
              Tüm Odaları Görüntüle <span className="le-line" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ============ AMENITIES (editorial list) ============ */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <p className="serif-i text-[#b8893a] text-2xl mb-3">Ayrıcalıklar</p>
              <h2 className="font-display text-[2.4rem] md:text-5xl text-[#2b2620] font-500 leading-[1.05] mb-6">
                Odalarımızda her detay düşünüldü
              </h2>
              <div className="rule-h max-w-[100px] mb-6" />
              <p className="text-[#6b6459] leading-[1.9] text-[14px] max-w-sm">
                Sizin ve sevdiklerinizin rahatı için gereken her şey, incelikle
                seçilmiş dokunuşlarla bir araya getirildi.
              </p>
            </div>
          </div>
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12">
              {amenities.map((a, i) => {
                const Icon = iconMap[a.icon] || Sparkles;
                return (
                  <Reveal key={i} delay={(i % 2) * 60}>
                    <div className="flex items-start gap-5 py-6 border-b border-[#e9e0cf] group">
                      <Icon size={22} className="text-[#b8893a] mt-1 shrink-0 group-hover:scale-110 transition-transform duration-300" />
                      <div>
                        <h3 className="font-display text-xl text-[#2b2620] font-600">{a.title}</h3>
                        <p className="text-[12.5px] text-[#8a8073] mt-1 leading-relaxed">{a.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ RESTAURANT (dark editorial split) ============ */}
      <section className="bg-[#221e18] text-white overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative min-h-[380px] lg:min-h-[640px] showcase">
            <div className="showcase-img absolute inset-0">
              <img src={restaurant.cover} alt={restaurant.title} className="w-full h-full object-cover" />
            </div>
            <div className="absolute inset-0 bg-black/25" />
            <span className="index-num absolute bottom-6 left-8 text-white/15 text-[120px] font-600">02</span>
          </div>
          <div className="flex items-center px-6 md:px-16 py-20 lg:py-0">
            <Reveal className="max-w-lg">
              <p className="serif-i text-[#d9b877] text-2xl mb-3">Lezzet Durağı</p>
              <h2 className="font-display text-[2.6rem] md:text-6xl font-500 leading-[1.02] mb-7">
                {restaurant.title}
              </h2>
              <div className="rule-h max-w-[110px] mb-7" />
              <p className="text-white/70 leading-[1.95] text-[15px] mb-8">{restaurant.intro}</p>
              <div className="space-y-4 mb-10">
                {restaurant.menu.slice(0, 3).map((m, i) => (
                  <div key={i} className="flex items-baseline gap-3">
                    <span className="font-display text-lg text-white font-500">{m.name}</span>
                    <span className="flex-1 border-b border-dotted border-white/20" />
                    <span className="font-display text-lg text-[#d9b877]">{m.price}</span>
                  </div>
                ))}
              </div>
              <Link to="/restoran" className="link-elegant on-dark">
                Menüyü Keşfet <span className="le-line" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ SAFRANBOLU (editorial) ============ */}
      <section className="relative py-28 md:py-40">
        <span className="index-num absolute left-2 top-10 text-[26vw] md:text-[200px] font-600 pointer-events-none select-none opacity-60 hidden md:block">
          03
        </span>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center relative">
          <div className="lg:col-span-7 relative">
            <Reveal className="clip-reveal">
              <img src={safranbolu.hero} alt="Safranbolu" className="w-full h-[420px] md:h-[580px] object-cover" />
            </Reveal>
            <div className="absolute -bottom-10 right-4 md:-right-10 bg-[#fdfbf7] shadow-2xl p-7 md:p-9 max-w-[300px] border-l-2 border-[#b8893a]">
              <p className="serif-i text-[#2b2620] text-xl leading-relaxed">
                “Zamanın durduğu, tarihin yaşadığı şehir.”
              </p>
              <p className="eyebrow text-[#b8893a] mt-4 text-[10px]">UNESCO Dünya Mirası</p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <p className="serif-i text-[#b8893a] text-2xl mb-3">Keşfet</p>
            <h2 className="font-display text-[2.4rem] md:text-5xl text-[#2b2620] font-500 leading-[1.05] mb-6">
              Safranbolu'nun zamansız güzelliği
            </h2>
            <p className="text-[#6b6459] leading-[1.9] text-[14px] mb-8">{safranbolu.intro}</p>
            <div className="space-y-0">
              {safranbolu.attractions.map((a, i) => (
                <div key={i} className="flex items-start gap-5 py-4 border-b border-[#e9e0cf]">
                  <span className="index-num text-[#d9b877] text-2xl font-600 leading-none pt-1">0{i + 1}</span>
                  <div>
                    <h3 className="font-display text-lg text-[#2b2620] font-600">{a.name}</h3>
                    <p className="text-[12.5px] text-[#8a8073]">{a.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/safranbolu" className="link-elegant mt-8">
              Safranbolu'yu Keşfet <span className="le-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="relative py-28 bg-[#2b2620] text-center overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <Reveal>
            <p className="serif-i text-[#d9b877] text-2xl mb-4">Ayrıcalıklı Konaklama</p>
            <h2 className="font-display text-4xl md:text-6xl text-white font-500 leading-[1.05] mb-7">
              Safranbolu'da sizi ağırlamaktan mutluluk duyarız
            </h2>
            <div className="gold-divider mb-8" />
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => openReservation()} className="btn-gold px-9 py-4">
                Çevrimiçi Rezervasyon
              </button>
              <a
                href={`tel:${hotel.phones[0].replace(/\s/g, "")}`}
                className="px-9 py-4 border border-white/40 text-white text-[0.72rem] uppercase tracking-[0.16em] hover:bg-white hover:text-[#2b2620] transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Phone size={14} /> {hotel.phones[0]}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
